import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  fetchStagingMetricDefs,
  FieldAutomationDataType,
  FieldMappingConfig,
  StagingMetricDef,
  TransformationConfig,
} from '../../../../services/queries/FileIngestionQueries';
import { fetchSnowflakeColumns } from '../../../../services/queries/SnowflakeSyncQueries';
import { allowedTargetTypes } from '../data-type';
import { useFieldOptions } from '../hooks/useFieldOptions';

const TARGET_TYPE_LABELS: Record<NonNullable<FieldMappingConfig['targetType']>, string> = {
  'entity-field': 'Entity Field',
  'custom-field': 'Custom Field',
  kpi: 'KPI',
  commentary: 'Commentary',
};
import { TransformationBuilder } from '../components/TransformationBuilder';
import { KPI_PERIODS, KPI_SECTIONS } from '../constants';

/** Per-field period/scenario override editor for KPI mappings */
function PerFieldOverrides({ basePath }: { basePath: string }) {
  const { control, register } = useFormContext();
  const {
    fields: periodFields,
    append: appendPeriod,
    remove: removePeriod,
  } = useFieldArray({ control, name: `${basePath}.periodLengthMappings` });
  const {
    fields: scenarioFields,
    append: appendScenario,
    remove: removeScenario,
  } = useFieldArray({ control, name: `${basePath}.scenarioMappings` });

  return (
    <Box mb={3}>
      <Typography variant='subtitle2' gutterBottom>
        Period/Scenario Overrides (Optional)
      </Typography>
      <Typography variant='body2' color='text.secondary' gutterBottom sx={{ mb: 2 }}>
        Override global period/scenario mappings for this specific field
      </Typography>

      {/* Per-field Period Mappings */}
      <Box mb={2}>
        <Typography variant='body2' fontWeight={600} gutterBottom>
          Period Mapping
        </Typography>
        <Stack spacing={1}>
          {periodFields.map((field, pIdx) => (
            <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
              <TextField
                {...register(`${basePath}.periodLengthMappings.${pIdx}.source`)}
                label='Source Period'
                placeholder='e.g., Quarterly'
                size='small'
                sx={{ flex: 1 }}
              />
              <Controller
                name={`${basePath}.periodLengthMappings.${pIdx}.target`}
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} select label='KPI Period' size='small' sx={{ flex: 1 }}>
                    {KPI_PERIODS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <IconButton onClick={() => removePeriod(pIdx)} size='small'>
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={() => appendPeriod({ source: '', target: '' })}
          variant='text'
          size='small'
          sx={{ mt: 1 }}
        >
          Add Period Override
        </Button>
      </Box>

      {/* Per-field Scenario Mappings */}
      <Box>
        <Typography variant='body2' fontWeight={600} gutterBottom>
          Scenario Mapping
        </Typography>
        <Stack spacing={1}>
          {scenarioFields.map((field, sIdx) => (
            <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
              <TextField
                {...register(`${basePath}.scenarioMappings.${sIdx}.source`)}
                label='Source Scenario'
                placeholder='e.g., Forecast'
                size='small'
                sx={{ flex: 1 }}
              />
              <Controller
                name={`${basePath}.scenarioMappings.${sIdx}.target`}
                control={control}
                render={({ field: f }) => (
                  <TextField {...f} select label='KPI Section' size='small' sx={{ flex: 1 }}>
                    {KPI_SECTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <IconButton onClick={() => removeScenario(sIdx)} size='small'>
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={() => appendScenario({ source: '', target: '' })}
          variant='text'
          size='small'
          sx={{ mt: 1 }}
        >
          Add Scenario Override
        </Button>
      </Box>
    </Box>
  );
}

const MappingGrid = styled('div')`
  display: grid;
  grid-template-columns: 2fr 1.5fr 2fr 1.5fr auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  background: ${({ theme }) => theme.palette.background.paper};
  margin-bottom: 0.5rem;
`;

const HeaderGrid = styled('div')`
  display: grid;
  grid-template-columns: 2fr 1.5fr 2fr 1.5fr auto auto;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  padding-left: calc(0.75rem + 1px);
`;

const TransformationsContainer = styled('div')`
  grid-column: 1 / -1;
  margin-top: 0.5rem;
`;

// ─── File-based Field Mappings ────────────────────────────────────────────────

function FileMappingsSection() {
  const { control, register, watch, setValue } = useFormContext();
  const { companyEntityFields, roundEntityFields, customFields, kpis, hasError, errorMessage } =
    useFieldOptions();
  const { colors } = useTheme();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  const rowStructure = watch('config.fileLevel.rowStructure');
  const targetEntity = watch('config.entityMapping.targetEntity');
  const entityFields = targetEntity === 'round' ? roundEntityFields : companyEntityFields;

  // Data Type (FD-3697) constrains what NEW mappings may target. Existing mappings are
  // grandfathered: a loaded mapping whose targetType is outside the allowed set keeps
  // rendering (its ORIGINAL value is unioned into the options — sourced from the
  // useFieldArray snapshot, not live form state, so a mis-click can be undone) and is
  // never rewritten. Form validity requires the final set to agree with the data type.
  const dataType = (watch('config.dataType') as FieldAutomationDataType | undefined) ?? 'entity-properties';
  const isEntityProperties = dataType === 'entity-properties';
  const isCommentary = dataType === 'commentary';
  const targetTypeOptions = allowedTargetTypes(dataType);
  const defaultTargetType = targetTypeOptions[0];

  // Snowflake-mode integration: when sourceType is 'snowflake' and an admin has
  // already picked a connection + table in the Ingestion Settings tab, fetch the
  // table's column list so the per-mapping `sourceColumn` input becomes a typed
  // dropdown rather than free text. Falls back to a plain TextField when columns
  // aren't (yet) available — e.g. mid-edit before the table is picked, or if the
  // columns endpoint errors out.
  const sourceType = watch('config.sourceType');
  const isSnowflake = sourceType === 'snowflake';
  const snowflakeConnectionId = watch('config.snowflakeConfig.connectionId') as number | undefined;
  const snowflakeSourceTable = watch('config.snowflakeConfig.sourceTable') as string | undefined;
  const snowflakeColumnsQuery = useQuery({
    queryKey: ['snowflake-columns', snowflakeConnectionId, snowflakeSourceTable],
    queryFn: () => fetchSnowflakeColumns(snowflakeConnectionId!, snowflakeSourceTable!),
    enabled: isSnowflake && !!snowflakeConnectionId && !!snowflakeSourceTable,
  });
  const snowflakeColumnNames = (snowflakeColumnsQuery.data ?? []).map((c) => c.name);
  const useSnowflakeColumnPicker = isSnowflake && snowflakeColumnNames.length > 0;

  const { fields, append, remove } = useFieldArray({ control, name: 'config.fieldMappings' });

  const handleAddMapping = () => {
    append({
      sourceColumn: '',
      targetType: defaultTargetType,
      entityField: '',
      kpiId: undefined,
      transformations: [{ type: 'direct', parameters: {} }],
    });
  };

  const handleAddSourceColumn = (mappingIndex: number) => {
    const currentValue = watch(`config.fieldMappings.${mappingIndex}.sourceColumn`);
    if (typeof currentValue === 'string') {
      setValue(`config.fieldMappings.${mappingIndex}.sourceColumn`, [currentValue, ''], {
        shouldDirty: true,
      });
    } else if (Array.isArray(currentValue)) {
      setValue(`config.fieldMappings.${mappingIndex}.sourceColumn`, [...currentValue, ''], {
        shouldDirty: true,
      });
    } else {
      setValue(`config.fieldMappings.${mappingIndex}.sourceColumn`, ['', ''], { shouldDirty: true });
    }
  };

  const handleRemoveSourceColumn = (mappingIndex: number, columnIndex: number) => {
    const currentValue = watch(`config.fieldMappings.${mappingIndex}.sourceColumn`);
    if (Array.isArray(currentValue)) {
      const newValue = currentValue.filter((_, i) => i !== columnIndex);
      setValue(
        `config.fieldMappings.${mappingIndex}.sourceColumn`,
        newValue.length === 1 ? newValue[0] : newValue,
        { shouldDirty: true }
      );
    }
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleTransformationTypeChange = (index: number, newType: string) => {
    const transformation: TransformationConfig = {
      type: newType as TransformationConfig['type'],
      parameters: {},
    };
    setValue(`config.fieldMappings.${index}.transformations`, [transformation], { shouldDirty: true });
    if (newType !== 'direct') {
      setExpandedRows((prev) => {
        const s = new Set(prev);
        s.add(index);
        return s;
      });
    }
  };

  // Default UNSET mappings to the data type's target. Never rewrites a mapping that
  // already has a targetType — existing choices are grandfathered even when the data
  // type changes mid-edit (the admin re-targets explicitly if that's what they want).
  useLayoutEffect(() => {
    if (!fields || fields.length === 0) {
      return;
    }
    fields.forEach((_, index) => {
      const currentTargetType = watch(`config.fieldMappings.${index}.targetType`);
      if (!currentTargetType) {
        // shouldDirty: false — this runs on mount for legacy mappings lacking a
        // targetType; defaulting one in must not dirty the form from merely viewing it.
        setValue(`config.fieldMappings.${index}.targetType`, defaultTargetType, { shouldDirty: false });
      }
    });
  }, [defaultTargetType, fields, setValue, watch]);

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h6' gutterBottom>
            Field Mappings
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            {isCommentary
              ? 'Pick the column(s) that hold the commentary text — each row is written to the matched company and reporting period.'
              : 'Map columns from the source to Foresight fields, custom fields, or KPIs'}
          </Typography>
        </Box>

        {hasError && errorMessage && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box>
          <HeaderGrid>
            <Typography variant='caption' color={colors.secondary?.[60] || 'secondary'} fontWeight={600}>
              Source Column
            </Typography>
            <Typography variant='caption' color={colors.secondary?.[60] || 'secondary'} fontWeight={600}>
              {isCommentary ? 'Destination' : 'Target Type'}
            </Typography>
            <Typography variant='caption' color={colors.secondary?.[60] || 'secondary'} fontWeight={600}>
              {isCommentary ? '' : 'Target Field'}
            </Typography>
            <Typography variant='caption' color={colors.secondary?.[60] || 'secondary'} fontWeight={600}>
              {isCommentary ? '' : 'Transformation'}
            </Typography>
            <Box sx={{ width: '40px' }} />
            <Box sx={{ width: '40px' }} />
          </HeaderGrid>

          {fields.map((field, index) => {
            const targetType = watch(`config.fieldMappings.${index}.targetType`);
            // Snapshot of the mapping's target when it was loaded/added (useFieldArray
            // item, not live form state) — keeps a grandfathered option in the menu even
            // after a mis-click changes the live value, so the change can be undone.
            const initialTargetType = (field as FieldMappingConfig & { id: string }).targetType;
            const transformations = watch(`config.fieldMappings.${index}.transformations`) || [];
            const isExpanded = expandedRows.has(index);
            const sourceColumn = watch(`config.fieldMappings.${index}.sourceColumn`);
            const isMultiColumn = Array.isArray(sourceColumn);
            const currentTransformationType =
              transformations.length > 0 && transformations[0].type ? transformations[0].type : 'direct';

            return (
              <MappingGrid key={field.id}>
                <Box>
                  {!isMultiColumn ? (
                    <Stack direction='row' spacing={1} alignItems='center'>
                      {useSnowflakeColumnPicker ? (
                        <Controller
                          name={`config.fieldMappings.${index}.sourceColumn`}
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              options={snowflakeColumnNames}
                              value={typeof field.value === 'string' && field.value ? field.value : null}
                              onChange={(_, next) => field.onChange(next ?? undefined)}
                              size='small'
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} placeholder='Snowflake column' size='small' />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          {...register(`config.fieldMappings.${index}.sourceColumn`)}
                          placeholder={isSnowflake ? 'Pick a Snowflake table first' : 'Column name from file'}
                          fullWidth
                          size='small'
                        />
                      )}
                      <IconButton
                        onClick={() => handleAddSourceColumn(index)}
                        size='small'
                        title='Add another column'
                      >
                        <AddIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack spacing={1}>
                      {sourceColumn.map((_col, colIndex) => {
                        const fieldPath = `config.fieldMappings.${index}.sourceColumn.${colIndex}` as const;
                        return (
                          <Stack key={fieldPath} direction='row' spacing={1} alignItems='center'>
                            {useSnowflakeColumnPicker ? (
                              <Controller
                                name={fieldPath}
                                control={control}
                                render={({ field }) => (
                                  <Autocomplete
                                    options={snowflakeColumnNames}
                                    value={
                                      typeof field.value === 'string' && field.value ? field.value : null
                                    }
                                    onChange={(_, next) => field.onChange(next ?? undefined)}
                                    size='small'
                                    fullWidth
                                    renderInput={(params) => (
                                      <TextField {...params} placeholder='Snowflake column' size='small' />
                                    )}
                                  />
                                )}
                              />
                            ) : (
                              <TextField
                                {...register(fieldPath)}
                                placeholder='Column name'
                                fullWidth
                                size='small'
                              />
                            )}
                            {sourceColumn.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveSourceColumn(index, colIndex)}
                                size='small'
                                title='Remove column'
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            )}
                          </Stack>
                        );
                      })}
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <IconButton
                          onClick={() => handleAddSourceColumn(index)}
                          size='small'
                          title='Add another column'
                        >
                          <AddIcon />
                        </IconButton>
                        <TextField
                          {...register(`config.fieldMappings.${index}.concatenationSeparator`)}
                          placeholder='Separator (e.g., ", ")'
                          size='small'
                          sx={{ width: '200px' }}
                          helperText='Separator between values'
                        />
                      </Stack>
                    </Stack>
                  )}
                </Box>

                {/* Commentary configs have no target picking — (entity, reporting period)
                    is the destination, so the row collapses to source column + caption. */}
                {!isCommentary && (
                  <Controller
                    name={`config.fieldMappings.${index}.targetType`}
                    control={control}
                    render={({ field }) => {
                      // Union the row's ORIGINAL target (load/add-time snapshot) and its
                      // live value into the options: grandfathered targets keep rendering
                      // and remain re-selectable after a mis-click.
                      const options = [
                        ...new Set(
                          [
                            initialTargetType,
                            field.value as FieldMappingConfig['targetType'],
                            ...targetTypeOptions,
                          ].filter((tt): tt is NonNullable<FieldMappingConfig['targetType']> => !!tt)
                        ),
                      ];
                      return (
                        <FormControl fullWidth size='small'>
                          <Select {...field} displayEmpty>
                            {options.map((tt) => (
                              <MenuItem key={tt} value={tt}>
                                {TARGET_TYPE_LABELS[tt] ?? tt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  />
                )}

                {targetType === 'commentary' && (
                  <Box sx={{ gridColumn: isCommentary ? 'span 2' : 'span 1', alignSelf: 'center' }}>
                    <Typography variant='caption' color='text.secondary'>
                      Writes to performance commentary at (entity, reporting period). No destination field
                      required.
                    </Typography>
                  </Box>
                )}

                {targetType === 'entity-field' && (
                  <Controller
                    name={`config.fieldMappings.${index}.entityField`}
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        options={entityFields}
                        getOptionLabel={(option) =>
                          typeof option === 'string'
                            ? entityFields.find((f) => f.id === option)?.displayName || option
                            : option.displayName
                        }
                        onChange={(_, value) => field.onChange(value ? value.id : '')}
                        value={entityFields.find((f) => f.id === field.value) || null}
                        renderInput={(params) => (
                          <TextField {...params} placeholder='Select entity field' size='small' />
                        )}
                        fullWidth
                      />
                    )}
                  />
                )}

                {targetType === 'custom-field' && (
                  <Controller
                    name={`config.fieldMappings.${index}.entityField`}
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        options={customFields}
                        getOptionLabel={(option) =>
                          typeof option === 'string' || typeof option === 'number'
                            ? customFields.find((f) => f.id === option)?.displayName || String(option)
                            : option.displayName
                        }
                        renderOption={(props, option) => {
                          const isArray =
                            option.dataType === 'stringArray' ||
                            option.dataType === 'numberArray' ||
                            option.dataType === 'array';
                          return (
                            <Box component='li' {...props}>
                              <Stack direction='row' spacing={1} alignItems='center'>
                                <span>{option.displayName}</span>
                                {isArray && (
                                  <Chip label='Array' size='small' color='primary' variant='outlined' />
                                )}
                              </Stack>
                            </Box>
                          );
                        }}
                        onChange={(_, value) => field.onChange(value ? value.id : '')}
                        value={customFields.find((f) => f.id === field.value) || null}
                        renderInput={(params) => (
                          <TextField {...params} placeholder='Select custom field' size='small' />
                        )}
                        fullWidth
                      />
                    )}
                  />
                )}

                {targetType === 'kpi' && (
                  <Controller
                    name={`config.fieldMappings.${index}.kpiId`}
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        options={kpis}
                        getOptionLabel={(option) =>
                          typeof option === 'number'
                            ? kpis.find((k) => k.id === option)?.displayName || String(option)
                            : option.displayName
                        }
                        onChange={(_, value) => field.onChange(value ? value.id : undefined)}
                        value={kpis.find((k) => k.id === field.value) || null}
                        renderInput={(params) => (
                          <TextField {...params} placeholder='Select KPI' size='small' />
                        )}
                        fullWidth
                      />
                    )}
                  />
                )}

                {/* Commentary rows collapse to source + caption + delete — no
                    transformation or expandable options. Empty boxes keep the grid aligned. */}
                {isCommentary ? (
                  <>
                    <Box />
                    <Box sx={{ width: '40px' }} />
                  </>
                ) : (
                  <>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={currentTransformationType}
                        onChange={(e) => handleTransformationTypeChange(index, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value='direct'>None</MenuItem>
                        <MenuItem value='conditional'>Conditional</MenuItem>
                        <MenuItem value='calculated'>Calculated</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton onClick={() => toggleExpanded(index)} size='small'>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </>
                )}

                <IconButton onClick={() => remove(index)} size='small' color='error'>
                  <DeleteOutlineIcon />
                </IconButton>

                <Collapse in={isExpanded && !isCommentary} timeout='auto' unmountOnExit>
                  <TransformationsContainer>
                    {isEntityProperties && rowStructure === 'multiple-rows-per-entity' && (
                      <Box mb={3}>
                        <Typography variant='subtitle2' gutterBottom>
                          Row Filtering (Optional)
                        </Typography>
                        <Typography variant='body2' color='text.secondary' gutterBottom sx={{ mb: 2 }}>
                          Filter which rows to include when aggregating values into arrays
                        </Typography>
                        <Stack spacing={2}>
                          <TextField
                            {...register(`config.fieldMappings.${index}.rowFilterColumn`)}
                            label='Filter Column'
                            placeholder='e.g., Role, Status'
                            fullWidth
                            size='small'
                            helperText='Column name to check for filtering'
                          />
                          <TextField
                            {...register(`config.fieldMappings.${index}.rowFilterValue`)}
                            label='Filter Value'
                            placeholder='e.g., Director, Active'
                            fullWidth
                            size='small'
                            helperText='Value to match (only rows with this value will be included)'
                          />
                        </Stack>
                      </Box>
                    )}
                    {targetType === 'kpi' && <PerFieldOverrides basePath={`config.fieldMappings.${index}`} />}
                    <Typography variant='subtitle2' gutterBottom>
                      Transformation
                    </Typography>
                    <TransformationBuilder mappingIndex={index} />
                  </TransformationsContainer>
                </Collapse>
              </MappingGrid>
            );
          })}
        </Box>

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddMapping}
          variant='outlined'
          size='medium'
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Field Mapping
        </Button>
      </Stack>
    </Box>
  );
}

// ─── API Integration Metric Mappings ─────────────────────────────────────────

function ApiMappingsSection() {
  const { control, watch } = useFormContext();
  const { customFields, kpis } = useFieldOptions();
  const [expandedMetrics, setExpandedMetrics] = useState<Set<number>>(() => new Set());

  const integrationProvider = watch('config.integrationProvider') as string | undefined;

  // Metric mappings follow the config's Data Type just like file/snowflake mappings:
  // Company Financials targets KPIs, Entity Properties targets custom fields. Existing
  // off-type mappings are grandfathered (their value is unioned into the row's options).
  const dataType = (watch('config.dataType') as FieldAutomationDataType | undefined) ?? 'entity-properties';
  const allowedMetricTargets: ('kpi' | 'custom-field')[] =
    dataType === 'company-financials' ? ['kpi'] : ['custom-field'];

  const { data: stagingMetricDefs = [] } = useQuery({
    queryKey: ['staging-metric-defs', integrationProvider],
    queryFn: () => fetchStagingMetricDefs(integrationProvider!),
    enabled: !!integrationProvider,
  });

  const defByExternalId = new Map<string, StagingMetricDef>(stagingMetricDefs.map((d) => [d.externalId, d]));

  const {
    fields: metricFields,
    append: appendMetric,
    remove: removeMetric,
  } = useFieldArray({
    control,
    name: 'config.integrationSync.metricMappings',
  });

  const toggleMetricExpanded = (index: number) => {
    setExpandedMetrics((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Stack spacing={6}>
      {/* Metric Mappings */}
      <Box>
        <Typography variant='h6' gutterBottom>
          Field Mappings
        </Typography>
        <Typography variant='body2' color='text.secondary' gutterBottom>
          Map each staging metric definition to a KPI or Custom Field. Only mapped metrics will be synced.
        </Typography>

        {metricFields.length > 0 && (
          <Stack direction='row' spacing={1} sx={{ mb: 1, px: 0.5 }}>
            <Typography variant='caption' sx={{ flex: 4 }}>
              Staging Metric Def
            </Typography>
            <Typography variant='caption' sx={{ flex: 3 }}>
              Target Type
            </Typography>
            <Typography variant='caption' sx={{ flex: 4 }}>
              Target KPI / Custom Field
            </Typography>
            <Box sx={{ width: 64 }} />
          </Stack>
        )}

        <Stack spacing={1}>
          {metricFields.map((field, index) => {
            const targetType = watch(`config.integrationSync.metricMappings.${index}.targetType`) as
              'kpi' | 'custom-field';
            const targetOptions = targetType === 'kpi' ? kpis : customFields;
            const isMetricExpanded = expandedMetrics.has(index);

            return (
              <Box key={field.id}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Controller
                    name={`config.integrationSync.metricMappings.${index}.stagingMetricDefId`}
                    control={control}
                    render={({ field: f }) => (
                      <Autocomplete
                        options={stagingMetricDefs}
                        getOptionLabel={(opt) => opt.name}
                        groupBy={(opt) => opt.category || 'Uncategorized'}
                        value={defByExternalId.get(f.value) ?? null}
                        onChange={(_, val) => f.onChange(val?.externalId ?? '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Metric Def'
                            size='small'
                            placeholder='Search metric defs…'
                          />
                        )}
                        sx={{ flex: 4 }}
                        isOptionEqualToValue={(opt, val) => opt.externalId === val.externalId}
                      />
                    )}
                  />

                  <Controller
                    name={`config.integrationSync.metricMappings.${index}.targetType`}
                    control={control}
                    render={({ field: f }) => {
                      const options = allowedMetricTargets.includes(f.value)
                        ? allowedMetricTargets
                        : [f.value as 'kpi' | 'custom-field', ...allowedMetricTargets];
                      return (
                        <TextField {...f} select label='Type' size='small' sx={{ flex: 3 }}>
                          {options.map((tt) => (
                            <MenuItem key={tt} value={tt}>
                              {TARGET_TYPE_LABELS[tt]}
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }}
                  />

                  <Controller
                    name={`config.integrationSync.metricMappings.${index}.targetId`}
                    control={control}
                    render={({ field: f }) => (
                      <Autocomplete
                        options={targetOptions}
                        getOptionLabel={(opt) => opt.displayName}
                        value={targetOptions.find((o) => o.id === f.value) ?? null}
                        onChange={(_, val) => f.onChange(val?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={targetType === 'kpi' ? 'KPI' : 'Custom Field'}
                            size='small'
                            placeholder='Search…'
                          />
                        )}
                        sx={{ flex: 4 }}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      />
                    )}
                  />

                  <IconButton onClick={() => toggleMetricExpanded(index)} size='small'>
                    {isMetricExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>

                  <IconButton onClick={() => removeMetric(index)} size='small'>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Stack>
                <Collapse in={isMetricExpanded} timeout='auto' unmountOnExit>
                  <Box sx={{ pl: 2, pt: 1 }}>
                    <PerFieldOverrides basePath={`config.integrationSync.metricMappings.${index}`} />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>

        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            appendMetric({ stagingMetricDefId: '', targetType: allowedMetricTargets[0], targetId: null })
          }
          variant='outlined'
          size='small'
          sx={{ mt: 2 }}
        >
          Add Mapping
        </Button>
      </Box>
    </Stack>
  );
}

// ─── Public export ────────────────────────────────────────────────────────────

export function FieldMappingsSection() {
  const { watch } = useFormContext();
  const sourceType = watch('config.sourceType');

  if (sourceType === 'api-integration') {
    return <ApiMappingsSection />;
  }

  return <FileMappingsSection />;
}
